import type { EvolutionSettings } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, PasswordInput, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

export const evolutionSettingsQuery = queryOptions({
  queryKey: ['platform', 'settings', 'evolution'],
  queryFn: () => api<EvolutionSettings>('/platform/settings/evolution'),
});
const when = (iso: string) => new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

/** Console → Configurações → WhatsApp: endereço e chave global da Evolution API (vale para todas as clínicas). */
export function EvolutionSettingsCard() {
  const qc = useQueryClient();
  const { data, isLoading, refetch, isFetching } = useQuery(evolutionSettingsQuery);
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  useEffect(() => { if (data?.url) setUrl(data.url); }, [data?.url]);
  const save = useMutation({
    mutationFn: () => api<EvolutionSettings>('/platform/settings/evolution', { method: 'PUT', body: { url, ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}) } }),
    onSuccess: (res) => { qc.setQueryData(evolutionSettingsQuery.queryKey, res); setApiKey(''); },
  });

  if (isLoading || !data) return <Card className="mt-4 grid h-32 place-items-center"><Spinner className="size-5 text-fg-subtle" label="Carregando" /></Card>;
  const status = !data.configured ? <Badge tone="warning" className="shrink-0 whitespace-nowrap">Não configurado</Badge>
    : data.reachable ? <Badge tone="success" className="shrink-0">Conectado</Badge>
    : <Badge tone="danger" className="shrink-0 whitespace-nowrap">Sem resposta</Badge>;

  return (
    <Card className="mt-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-fg"><MessageCircle className="size-4 text-fg-subtle" />WhatsApp (Evolution API)</h2>
          <p className="mt-1 text-[13px] text-fg-muted">Servidor que conecta os números das clínicas. Depois de configurar, conecte cada número na página da clínica, aba WhatsApp.</p>
        </div>
        {status}
      </div>

      {data.configured && (
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 rounded-lg bg-surface-muted p-3.5 text-[13px]">
          <dt className="text-fg-muted">Endereço</dt><dd className="font-mono break-all text-fg">{data.url}</dd>
          <dt className="text-fg-muted">Chave</dt><dd className="font-mono text-fg">{data.keyHint}</dd>
          {data.reachable && <><dt className="text-fg-muted">Instâncias</dt><dd className="text-fg">{data.instances} no servidor</dd></>}
          {data.error && <><dt className="text-fg-muted">Erro</dt><dd className="text-danger">{data.error}</dd></>}
          <dt className="text-fg-muted">Origem</dt>
          <dd className="text-fg">{data.source === 'console' ? `Console${data.updatedAt ? ` · ${when(data.updatedAt)}${data.updatedBy ? ` por ${data.updatedBy}` : ''}` : ''}` : 'Variável de ambiente do servidor'}</dd>
        </dl>
      )}

      <form className="mt-5 space-y-4" noValidate onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Endereço da Evolution API" hint="URL pública do servidor, ex.: https://evolution.suaempresa.com.br">
          <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" spellCheck={false} className="font-mono" />
        </Field>
        <Field label="Chave global (AUTHENTICATION_API_KEY)" hint="A chave definida na instalação da Evolution. Fica cifrada e nunca é mostrada de novo.">
          <PasswordInput autoComplete="off" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={data.configured ? 'Deixe em branco para manter a atual' : ''} />
        </Field>
        {save.error && <Alert tone="danger">{save.error.message}</Alert>}
        {save.isSuccess && <Alert tone="success">Configuração salva. A Evolution respondeu com a chave informada.</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={save.isPending} disabled={!url || (!data.configured && !apiKey.trim())}>Salvar e testar</Button>
          {data.configured && <Button variant="secondary" loading={isFetching} onClick={() => void refetch()}><RefreshCw className="size-4" />Testar de novo</Button>}
        </div>
      </form>
    </Card>
  );
}
